const GOOGLE_MAPS_SCRIPT_ID = 'google-maps-places';

/** Poll `check` until truthy or `timeoutMs` elapses. Rejects on timeout so
 *  callers don't hang forever if Google Maps init fails silently (invalid key,
 *  blocked by CSP, etc.). */
function waitFor(check: () => boolean, timeoutMs = 10_000): Promise<void> {
  if (check()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      if (check()) {
        clearInterval(interval);
        clearTimeout(timeout);
        resolve();
      }
    }, 50);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      reject(new Error('Google Maps library failed to initialize'));
    }, timeoutMs);
  });
}

export function loadGoogleMapsScript(apiKey: string): Promise<void> {
  // Issue #1345: with `&loading=async`, `script.onload` fires before the
  // `places` library finishes initializing. Probe the actual constructor
  // (not just the namespace) so callers can `new` it immediately after this
  // promise resolves.
  const isReady = () => !!window.google?.maps?.places?.AutocompleteService;

  if (isReady()) return Promise.resolve();

  if (document.getElementById(GOOGLE_MAPS_SCRIPT_ID)) {
    return waitFor(isReady);
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = GOOGLE_MAPS_SCRIPT_ID;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.onload = () => waitFor(isReady).then(resolve, reject);
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
}

/**
 * Load Google Maps for the ScreenMap component.
 * Ensures the Map constructor is available (not just the bootstrap stub).
 */
export function loadGoogleMapsWithMarker(apiKey: string): Promise<void> {
  if (window.google?.maps?.Map) return Promise.resolve();

  // If script is already loading/loaded, just wait for Map to be ready
  if (document.getElementById(GOOGLE_MAPS_SCRIPT_ID)) {
    return waitFor(() => !!window.google?.maps?.Map);
  }

  // Load the script then wait for Map constructor
  return loadGoogleMapsScript(apiKey).then(() => waitFor(() => !!window.google?.maps?.Map));
}

/**
 * Preload the Google Maps script (call on hover/mount to reduce perceived latency).
 * Non-blocking — doesn't return a promise.
 */
export function preloadGoogleMapsScript(apiKey: string): void {
  if (!apiKey || window.google?.maps || document.getElementById(GOOGLE_MAPS_SCRIPT_ID)) return;

  const script = document.createElement('script');
  script.id = GOOGLE_MAPS_SCRIPT_ID;
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
  script.async = true;
  document.head.appendChild(script);
}
